import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import AdminLayout from '../../components/admin/AdminLayout';
import { adminApi, publicAssetUrl } from '../../lib/adminApi';

const emptyProgramme = {
  programme_name: '',
  award_type: 'ND',
  duration: '',
  entry_requirements: '',
};

export default function AdminDepartmentForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(Boolean(id));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [heroPreview, setHeroPreview] = useState('');
  const [hodPreview, setHodPreview] = useState('');

  const [form, setForm] = useState({
    name: '',
    school: '',
    description: '',
    vision: '',
    mission: '',
    hod_name: '',
    status: 'active',
    hero_image: null,
    hod_photo: null,
    programmes: [{ ...emptyProgramme }],
  });

  useEffect(() => {
    if (!id) return;

    async function loadDepartment() {
      try {
        setLoading(true);
        setError('');
        const data = await adminApi(`/api/admin/departments/${id}`);
        setForm({
          name: data.name || '',
          school: data.school || '',
          description: data.description || '',
          vision: data.vision || '',
          mission: data.mission || '',
          hod_name: data.hod_name || '',
          status: data.status || 'active',
          hero_image: null,
          hod_photo: null,
          programmes: data.programmes?.length ? data.programmes.map((item) => ({
            programme_name: item.programme_name || '',
            award_type: item.award_type || 'ND',
            duration: item.duration || '',
            entry_requirements: item.entry_requirements || '',
          })) : [{ ...emptyProgramme }],
        });
        setHeroPreview(data.hero_image ? publicAssetUrl(data.hero_image) : '');
        setHodPreview(data.hod_photo ? publicAssetUrl(data.hod_photo) : '');
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    }

    loadDepartment();
  }, [id]);

  function updateField(e) {
    const { name, value, files, type } = e.target;
    if (type === 'file') {
      const file = files?.[0] || null;
      setForm((prev) => ({ ...prev, [name]: file }));
      if (file && name === 'hero_image') setHeroPreview(URL.createObjectURL(file));
      if (file && name === 'hod_photo') setHodPreview(URL.createObjectURL(file));
      return;
    }
    setForm((prev) => ({ ...prev, [name]: value }));
  }

  function updateProgramme(index, field, value) {
    setForm((prev) => {
      const programmes = [...prev.programmes];
      programmes[index] = { ...programmes[index], [field]: value };
      return { ...prev, programmes };
    });
  }

  function addProgramme() {
    setForm((prev) => ({ ...prev, programmes: [...prev.programmes, { ...emptyProgramme }] }));
  }

  function removeProgramme(index) {
    setForm((prev) => ({ ...prev, programmes: prev.programmes.filter((_, itemIndex) => itemIndex !== index) }));
  }

  async function submitForm(e) {
    e.preventDefault();
    try {
      setSaving(true);
      setError('');

      const body = new FormData();
      body.append('name', form.name);
      body.append('school', form.school);
      body.append('description', form.description);
      body.append('vision', form.vision);
      body.append('mission', form.mission);
      body.append('hod_name', form.hod_name);
      body.append('status', form.status);
      body.append('programmes', JSON.stringify(form.programmes.filter((item) => item.programme_name.trim())));
      if (form.hero_image) body.append('hero_image', form.hero_image);
      if (form.hod_photo) body.append('hod_photo', form.hod_photo);

      await adminApi(id ? `/api/admin/departments/${id}` : '/api/admin/departments', {
        method: id ? 'PUT' : 'POST',
        body,
      });

      navigate('/adxm/departments');
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return <AdminLayout title="Department CMS"><div className="rounded-xl bg-white p-6 text-sm font-bold text-slate-600 shadow-sm">Loading department...</div></AdminLayout>;
  }

  return (
    <AdminLayout
      title={id ? 'Edit Department' : 'Add Department'}
      subtitle="Create a dynamic public department page with programmes and HOD profile."
      actions={<Link to="/adxm/departments" className="rounded-xl border border-slate-200 px-4 py-3 text-sm font-black text-slate-700 hover:bg-slate-100">Back</Link>}
    >
      {error && <div className="mb-4 rounded-xl border border-red-200 bg-red-50 p-4 text-sm font-bold text-red-700">{error}</div>}

      <form onSubmit={submitForm} className="space-y-6">
        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
          <h2 className="text-lg font-black text-slate-950">Basic Information</h2>
          <div className="mt-5 grid gap-5 md:grid-cols-2">
            <label className="block md:col-span-2">
              <span className="text-sm font-bold text-slate-700">Department Name</span>
              <input name="name" value={form.name} onChange={updateField} className="mt-2 w-full rounded-xl border border-slate-200 px-4 py-3 outline-none focus:border-emerald-600" required />
            </label>

            <label className="block">
              <span className="text-sm font-bold text-slate-700">School / Faculty</span>
              <input name="school" value={form.school} onChange={updateField} className="mt-2 w-full rounded-xl border border-slate-200 px-4 py-3 outline-none focus:border-emerald-600" placeholder="School of ICT" />
            </label>

            <label className="block">
              <span className="text-sm font-bold text-slate-700">Status</span>
              <select name="status" value={form.status} onChange={updateField} className="mt-2 w-full rounded-xl border border-slate-200 px-4 py-3 outline-none focus:border-emerald-600">
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </label>

            <label className="block md:col-span-2">
              <span className="text-sm font-bold text-slate-700">Description</span>
              <textarea name="description" value={form.description} onChange={updateField} rows="5" className="mt-2 w-full rounded-xl border border-slate-200 px-4 py-3 outline-none focus:border-emerald-600" />
            </label>
          </div>
        </div>

        <div className="grid gap-6 lg:grid-cols-2">
          <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
            <h2 className="text-lg font-black text-slate-950">Vision & Mission</h2>
            <label className="mt-5 block">
              <span className="text-sm font-bold text-slate-700">Vision</span>
              <textarea name="vision" value={form.vision} onChange={updateField} rows="4" className="mt-2 w-full rounded-xl border border-slate-200 px-4 py-3 outline-none focus:border-emerald-600" />
            </label>
            <label className="mt-5 block">
              <span className="text-sm font-bold text-slate-700">Mission</span>
              <textarea name="mission" value={form.mission} onChange={updateField} rows="4" className="mt-2 w-full rounded-xl border border-slate-200 px-4 py-3 outline-none focus:border-emerald-600" />
            </label>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
            <h2 className="text-lg font-black text-slate-950">Images & HOD</h2>
            <label className="mt-5 block">
              <span className="text-sm font-bold text-slate-700">HOD Name</span>
              <input name="hod_name" value={form.hod_name} onChange={updateField} className="mt-2 w-full rounded-xl border border-slate-200 px-4 py-3 outline-none focus:border-emerald-600" />
            </label>
            <div className="mt-5 grid gap-4 md:grid-cols-2">
              <label className="block">
                <span className="text-sm font-bold text-slate-700">Hero Image</span>
                <input type="file" name="hero_image" accept="image/*" onChange={updateField} className="mt-2 w-full rounded-xl border border-slate-200 px-4 py-3 text-sm" />
                {heroPreview && <img src={heroPreview} alt="" className="mt-3 h-24 w-full rounded-xl object-cover" />}
              </label>
              <label className="block">
                <span className="text-sm font-bold text-slate-700">HOD Photo</span>
                <input type="file" name="hod_photo" accept="image/*" onChange={updateField} className="mt-2 w-full rounded-xl border border-slate-200 px-4 py-3 text-sm" />
                {hodPreview && <img src={hodPreview} alt="" className="mt-3 h-24 w-24 rounded-full object-cover" />}
              </label>
            </div>
          </div>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
          <div className="flex items-center justify-between gap-4">
            <h2 className="text-lg font-black text-slate-950">Programmes</h2>
            <button type="button" onClick={addProgramme} className="rounded-xl border border-emerald-200 px-4 py-2 text-sm font-black text-emerald-700 hover:bg-emerald-50">+ Add Programme</button>
          </div>

          <div className="mt-5 space-y-4">
            {form.programmes.map((programme, index) => (
              <div key={index} className="rounded-2xl border border-slate-200 p-4">
                <div className="grid gap-4 md:grid-cols-[1fr_140px_160px_auto]">
                  <input value={programme.programme_name} onChange={(e) => updateProgramme(index, 'programme_name', e.target.value)} placeholder="Programme name" className="rounded-xl border border-slate-200 px-4 py-3 outline-none focus:border-emerald-600" />
                  <select value={programme.award_type} onChange={(e) => updateProgramme(index, 'award_type', e.target.value)} className="rounded-xl border border-slate-200 px-4 py-3 outline-none focus:border-emerald-600">
                    <option value="ND">ND</option>
                    <option value="HND">HND</option>
                    <option value="Diploma">Diploma</option>
                    <option value="Certificate">Certificate</option>
                  </select>
                  <input value={programme.duration} onChange={(e) => updateProgramme(index, 'duration', e.target.value)} placeholder="Duration" className="rounded-xl border border-slate-200 px-4 py-3 outline-none focus:border-emerald-600" />
                  <button type="button" onClick={() => removeProgramme(index)} className="rounded-xl border border-red-200 px-4 py-3 text-sm font-black text-red-700 hover:bg-red-50">Remove</button>
                </div>
                <textarea value={programme.entry_requirements} onChange={(e) => updateProgramme(index, 'entry_requirements', e.target.value)} rows="3" placeholder="Entry requirements" className="mt-3 w-full rounded-xl border border-slate-200 px-4 py-3 outline-none focus:border-emerald-600" />
              </div>
            ))}
          </div>
        </div>

        <div className="flex justify-end gap-3">
          <Link to="/adxm/departments" className="rounded-xl border border-slate-200 px-5 py-3 font-black text-slate-700 hover:bg-slate-100">Cancel</Link>
          <button disabled={saving} className="rounded-xl bg-emerald-700 px-6 py-3 font-black text-white hover:bg-emerald-800 disabled:opacity-60">
            {saving ? 'Saving...' : 'Save Department'}
          </button>
        </div>
      </form>
    </AdminLayout>
  );
}
